import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { hasAnyPermission, permissionSet } from "@/lib/authz";
import {
  Plus,
  Search,
  Settings2,
  Trash2,
  ShoppingCart,
  Package,
  Globe,
  Truck,
  Loader2,
  CheckCircle2,
  Eye,
  EyeOff,
  HelpCircle,
  ArrowLeft,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

function StatusPill({ status, onClick }) {
  const isSuccess = status === "success" || status === "connected";
  const isPending = !isSuccess && status !== "failed";
  const isClickable = isPending && typeof onClick === "function";
  const cls = isSuccess
    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
    : status === "failed"
    ? "bg-rose-600 text-white ring-rose-600 hover:bg-rose-700"
    : "bg-rose-600 text-white ring-rose-600 hover:bg-rose-700";
  const label = isSuccess ? "Aktif" : status === "failed" ? "Hata" : "Kurulum";
  const Comp = isClickable ? "button" : "span";
  return (
    <Comp
      type={isClickable ? "button" : undefined}
      onClick={isClickable ? onClick : undefined}
      className={cn(
        "inline-flex h-9 items-center rounded-lg px-3 text-xs font-medium ring-1 transition-colors",
        cls,
        !isClickable && "cursor-default"
      )}
      data-testid={`status-pill-${status}`}
    >
      {isSuccess ? <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> : null}
      {label}
    </Comp>
  );
}

function CargoPill({ configured, provider }) {
  if (!configured) return null;
  const providerLabelMap = {
    hepsijet: "HepsiJet",
    yurtici: "Yurtici",
    dhl: "DHL",
    ups: "UPS",
    aras: "Aras",
    surat: "Surat",
  };
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700" data-testid="cargo-pill">
      {providerLabelMap[provider] || "Bagli"}
    </span>
  );
}

function ProviderPill({ provider }) {
  const map = {
    woocommerce: "WooCommerce",
    shopify: "Shopify",
    shopier: "Shopier",
    ikas: "ikas",
    opencart: "OpenCart",
    trendyol: "Trendyol",
    hepsiburada: "Hepsiburada",
    amazon: "Amazon",
    n11: "n11",
    pttavm: "PTTAVM",
    ciceksepeti: "Ciceksepeti",
    parasut: "Parasut",
    hepsijet: "HepsiJet",
    yurtici: "Yurtici Kargo",
    dhl: "DHL",
    ups: "UPS",
    aras: "Aras Kargo",
    surat: "Surat Kargo",
  };
  return (
    <span className="inline-flex rounded-full bg-muted/50 px-2 py-1 text-[11px] font-semibold text-foreground/80 ring-1 ring-border">
      {map[provider] || provider}
    </span>
  );
}

const integrationCatalog = [
  // Pazaryeri
  { value: "amazon", label: "Amazon", kind: "marketplace", logo: "AMZ", logoCls: "bg-amber-100 text-amber-800" },
  { value: "trendyol", label: "Trendyol", kind: "marketplace", logo: "TY", logoCls: "bg-orange-100 text-orange-800" },
  { value: "hepsiburada", label: "Hepsiburada", kind: "marketplace", logo: "HB", logoCls: "bg-red-100 text-red-800" },
  { value: "ciceksepeti", label: "Ciceksepeti", kind: "marketplace", logo: "CS", logoCls: "bg-rose-100 text-rose-800" },
  { value: "n11", label: "N11", kind: "marketplace", logo: "N11", logoCls: "bg-purple-100 text-purple-800" },
  { value: "pazarama", label: "Pazarama", kind: "marketplace", logo: "PZR", logoCls: "bg-blue-100 text-blue-800" },
  { value: "idefix", label: "Idefix", kind: "marketplace", logo: "IDX", logoCls: "bg-orange-100 text-orange-800" },
  { value: "pttavm", label: "PTTAVM", kind: "marketplace", logo: "PTT", logoCls: "bg-yellow-100 text-yellow-800" },

  // Kargo
  { value: "hepsijet", label: "HepsiJet", kind: "cargo", logo: "HJ", logoCls: "bg-teal-100 text-teal-800" },
  { value: "yurtici", label: "Yurtici Kargo", kind: "cargo", logo: "YK", logoCls: "bg-blue-100 text-blue-800" },
  { value: "dhl", label: "DHL", kind: "cargo", logo: "DHL", logoCls: "bg-yellow-100 text-yellow-900" },
  { value: "ups", label: "UPS", kind: "cargo", logo: "UPS", logoCls: "bg-amber-100 text-amber-900" },
  { value: "aras", label: "Aras Kargo", kind: "cargo", logo: "AR", logoCls: "bg-red-100 text-red-800" },
  { value: "surat", label: "Surat Kargo", kind: "cargo", logo: "SK", logoCls: "bg-cyan-100 text-cyan-800" },

  // E-Ticaret
  { value: "woocommerce", label: "WooCommerce", kind: "store", logo: "WOO", logoCls: "bg-violet-100 text-violet-800" },
  { value: "shopify", label: "Shopify", kind: "store", logo: "SH", logoCls: "bg-emerald-100 text-emerald-800" },
  { value: "shopier", label: "Shopier", kind: "store", logo: "SPI", logoCls: "bg-indigo-100 text-indigo-800" },
  { value: "ikas", label: "ikas", kind: "store", logo: "IK", logoCls: "bg-muted text-foreground" },
  { value: "opencart", label: "OpenCart", kind: "store", logo: "OC", logoCls: "bg-sky-100 text-sky-800" },
];

const storeProviderSet = new Set(["woocommerce", "shopify", "shopier", "ikas", "opencart", "local_ecommerce"]);
const cargoProviderSet = new Set(["hepsijet", "yurtici", "dhl", "ups", "aras", "surat"]);
const marketplaceSelfCargoProviders = new Set(["trendyol", "hepsiburada", "amazon"]);

const providerLogoMap = {
  hepsijet: "/integrations/hepsijet.svg",
  dhl: "/integrations/dhl.svg",
  surat: "/integrations/surat.svg",
  aras: "/integrations/aras.svg",
  ups: "/integrations/ups.svg",
  amazon: "/integrations/amazon.svg",
  yurtici: "/integrations/yurtici.svg",
  opencart: "/integrations/opencart.svg",
  ikas: "/integrations/ikas.svg",
  pttavm: "/integrations/pttavm.svg",
  shopier: "/integrations/shopier.svg",
  woocommerce: "/integrations/woocommerce.svg",
  shopify: "/integrations/shopify.svg",
  ciceksepeti: "/integrations/ciceksepeti.svg",
  hepsiburada: "/integrations/hepsiburada.svg",
  n11: "/integrations/n11.png",
  trendyol: "/integrations/trendyol.svg",
  pazarama: "/integrations/pazarama.svg",
  idefix: "/integrations/idefix.svg",
  benimpos: "/integrations/benimpos.png?v=20261030",
  "turkcell-efatura": "/integrations/turkcell-efatura.png",
};

function normalizeProviderKey(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw.includes("pazarama")) return "pazarama";
  if (raw.includes("hepsiburada")) return "hepsiburada";
  if (raw.includes("trendyol")) return "trendyol";
  if (raw === "woo") return "woocommerce";
  return raw;
}

function ProviderLogo({ provider, size = "default", framed = true }) {
  const sizeCls =
    size === "large" ? "h-20 w-20" : size === "small" ? "h-6 w-6" : "h-12 w-12";
  const normalizedProvider = normalizeProviderKey(provider);
  const src = providerLogoMap[normalizedProvider];
  const frameCls = framed
    ? "rounded-lg border border-border bg-card p-1"
    : "rounded-lg bg-transparent p-0";
  if (src) {
    return (
      <span className={cn("inline-flex items-center justify-center", frameCls, sizeCls)}>
        <img src={src} alt={provider} className="h-full w-full object-contain" />
      </span>
    );
  }
  const p = integrationCatalog.find((x) => x.value === normalizedProvider);
  const logo = p?.logo || normalizedProvider?.slice(0, 2)?.toUpperCase() || "?";
  return <span className={cn("inline-flex items-center justify-center rounded-lg border border-border bg-muted/60 text-[11px] font-bold text-foreground/80", sizeCls)}>{logo}</span>;
}

export default function IntegrationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [providerCatalog, setProviderCatalog] = useState([]);
  const [hepsijetConfig, setHepsijetConfig] = useState({ configured: false, integration_id: "" });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newIntegration, setNewIntegration] = useState({ provider: "" });
  const [selectedStoreForCargo, setSelectedStoreForCargo] = useState("");
  const [addingIntegration, setAddingIntegration] = useState(false);
  const [catalogFilter, setCatalogFilter] = useState("all");
  const [catalogQuery, setCatalogQuery] = useState("");

  // Cargo setup state
  const [cargoDialogOpen, setCargoDialogOpen] = useState(false);
  const [cargoStep, setCargoStep] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteNote, setDeleteNote] = useState("");
  const [deleteSubscriptionToo, setDeleteSubscriptionToo] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [pendingProvider, setPendingProvider] = useState(null);
  const [packageDialogMode, setPackageDialogMode] = useState("new"); // new | over_limit
  const [billingPackages, setBillingPackages] = useState([]);
  const [selectedBillingPlan, setSelectedBillingPlan] = useState("");
  const [selectedBillingCycle, setSelectedBillingCycle] = useState("monthly");
  const [cargoIntegration, setCargoIntegration] = useState(null);
  const [cargoForm, setCargoForm] = useState({
    provider: "hepsijet",
    integration_name: "HepsiJET",
    username: "",
    password: "",
    sender_address_id: "",
    current_xdock: "",
  });
  const [cargoAlreadyConfigured, setCargoAlreadyConfigured] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [cargoSaving, setCargoSaving] = useState(false);
  const [cargoTesting, setCargoTesting] = useState(false);
  const [usageSummary, setUsageSummary] = useState(null);
  const [serverSubscriptionPlan, setServerSubscriptionPlan] = useState("");
  const [serverSubscriptionCycle, setServerSubscriptionCycle] = useState("monthly");
  const [isTrialUser, setIsTrialUser] = useState(false);
  const INTEGRATIONS_CACHE_KEY = "obihub_integrations_cache";
  const INTEGRATIONS_CACHE_META_KEY = "obihub_integrations_cache_meta";
  const INTEGRATIONS_CACHE_TTL_MS = 2 * 60 * 1000;
  const fetchSeqRef = useRef(0);
  const canManageIntegrations = hasAnyPermission(permissionSet(user), ["integrations.manage"]);
  const currentPlan = String(serverSubscriptionPlan || user?.subscription_plan || "").toLowerCase();

  useEffect(() => {
    fetchProviderCatalog();
    fetchIntegrations();
  }, []);

  const normalizeIntegrationRows = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.integrations)) return payload.integrations;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.items)) return payload.data.items;
    if (Array.isArray(payload?.data?.integrations)) return payload.data.integrations;
    if (Array.isArray(payload?.result)) return payload.result;
    if (Array.isArray(payload?.result?.items)) return payload.result.items;
    if (Array.isArray(payload?.payload?.items)) return payload.payload.items;
    return [];
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get("/usage/summary");
        if (mounted) setUsageSummary(res || null);
      } catch {
        if (mounted) setUsageSummary(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get("/billing/trial-status");
        if (!mounted) return;
        setServerSubscriptionPlan(String(res?.plan || "").toLowerCase());
        setServerSubscriptionCycle(
          String(res?.billing_cycle || "monthly").toLowerCase() === "yearly" ? "yearly" : "monthly"
        );
        setIsTrialUser(Boolean(res?.is_trial));
      } catch {
        if (!mounted) return;
        setServerSubscriptionPlan(String(user?.subscription_plan || "").toLowerCase());
        setServerSubscriptionCycle("monthly");
        setIsTrialUser(String(user?.subscription_plan || "").toLowerCase() === "trial");
      }
    })();
    return () => {
      mounted = false;
    };
  }, [user?.subscription_plan]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get("/billing/packages");
        const packs = Array.isArray(res?.packages) ? res.packages : [];
        if (!mounted) return;
        setBillingPackages(packs);
        if (packs.length > 0) {
          setSelectedBillingPlan(packs.find((x) => x.highlight)?.key || packs[0].key || "");
        }
      } catch {
        // ignore
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!cargoDialogOpen || cargoForm.provider !== "hepsijet") return;
    (async () => {
      try {
        const cfg = await api.get("/hepsijet/config");
        if (!mounted) return;
        if (!cfg?.configured) {
          setCargoAlreadyConfigured(false);
          return;
        }

        // Reuse existing values as defaults, but only lock the UI when
        // explicitly requested by caller (openCargoDialog forceConfigured flag).
        // This keeps "Yeni HepsiJet ekle" flow in setup mode.
        setCargoForm((prev) => ({
          ...prev,
          integration_name: cfg.integration_name || prev.integration_name,
          sender_address_id: cfg.sender_address_id || "",
          current_xdock: cfg.current_xdock || "",
        }));
      } catch {
        // ignore
      }
    })();
    return () => {
      mounted = false;
    };
  }, [cargoDialogOpen, cargoForm.provider, cargoIntegration?.id]);

  const effectiveCatalog = useMemo(() => {
    const source = Array.isArray(providerCatalog) && providerCatalog.length
      ? providerCatalog
      : integrationCatalog;
    return source
      .filter((x) => String(x?.module_class || "integration") !== "application")
      .map((x) => ({
        value: String(x.value || x.key || "").toLowerCase(),
        label: String(x.label || x.value || x.key || "").trim(),
        kind: String(x.kind || "store").toLowerCase(),
      }))
      .filter((x) => Boolean(x.value));
  }, [providerCatalog]);

  const cargoProvidersResolved = useMemo(
    () => effectiveCatalog.filter((x) => x.kind === "cargo"),
    [effectiveCatalog]
  );

  const addableCatalog = effectiveCatalog;

  const fetchProviderCatalog = async () => {
    try {
      let res;
      try {
        res = await api.get("/integration-providers");
      } catch (primaryErr) {
        res = await api.get("/integrations/providers");
      }
      const items = Array.isArray(res?.items) ? res.items : [];
      setProviderCatalog(
        items.map((x) => ({
          key: x.key,
          value: x.key,
          label: x.label,
          kind: x.kind,
          module_class: x.module_class,
        }))
      );
    } catch (error) {
      console.error("Failed to fetch providers:", error);
      setProviderCatalog([]);
    }
  };

  const fetchIntegrations = async ({ force = false } = {}) => {
    const reqSeq = ++fetchSeqRef.current;
    if (!force) {
      try {
        const meta = JSON.parse(sessionStorage.getItem(INTEGRATIONS_CACHE_META_KEY) || "{}");
        const ts = Number(meta?.ts || 0);
        if (ts > 0 && Date.now() - ts < INTEGRATIONS_CACHE_TTL_MS) {
          const cached = JSON.parse(sessionStorage.getItem(INTEGRATIONS_CACHE_KEY) || "[]");
          if (Array.isArray(cached) && cached.length > 0) {
            setIntegrations(cached);
            setLoading(false);
            return;
          }
        }
      } catch {
        // ignore cache parse errors
      }
    }
    setLoading(true);
    try {
      let data;
      let lastErr = null;
      let retriesOnEmpty = 0;
      for (let attempt = 1; attempt <= 4; attempt += 1) {
        try {
          data = await api.get("/integrations", {
            headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
          });
          const probeRows = normalizeIntegrationRows(data);
          if (probeRows.length === 0 && retriesOnEmpty < 2) {
            retriesOnEmpty += 1;
            await sleep(350 * retriesOnEmpty);
            continue;
          }
          lastErr = null;
          break;
        } catch (err) {
          lastErr = err;
          if (attempt < 3) {
            await sleep(250 * attempt);
          }
        }
      }
      if (lastErr) throw lastErr;

      if (reqSeq !== fetchSeqRef.current) return;
      const rows = normalizeIntegrationRows(data);
      if (rows.length > 0) {
        setIntegrations(rows);
      } else {
        setIntegrations([]);
      }
      sessionStorage.setItem(INTEGRATIONS_CACHE_KEY, JSON.stringify(rows));
      sessionStorage.setItem(INTEGRATIONS_CACHE_META_KEY, JSON.stringify({ ts: Date.now() }));

      const hj = await api.get("/hepsijet/config").catch(() => ({ configured: false }));
      if (reqSeq !== fetchSeqRef.current) return;
      setHepsijetConfig(hj || { configured: false, integration_id: "" });
    } catch (error) {
      if (reqSeq !== fetchSeqRef.current) return;
      console.error("Failed to fetch integrations:", error);
      if (integrations.length === 0) {
        try {
          const cached = JSON.parse(sessionStorage.getItem(INTEGRATIONS_CACHE_KEY) || "[]");
          if (Array.isArray(cached) && cached.length > 0) {
            setIntegrations(cached);
          }
        } catch {
          // ignore cache parse errors
        }
      }
      toast.error("Entegrasyonlar yüklenemedi. Lütfen tekrar deneyin.");
    } finally {
      if (reqSeq !== fetchSeqRef.current) return;
      setLoading(false);
    }
  };

  const createIntegrationForProvider = async (providerValue, opts = {}) => {
    if (!canManageIntegrations) {
      toast.error("Bu işlem için entegrasyon yönetim yetkiniz yok.");
      return;
    }
    const provider = effectiveCatalog.find((p) => p.value === providerValue);
    if (!provider) return;
    setAddingIntegration(true);
    try {
      const sameProviderCount = integrations.filter((i) => i.provider === providerValue).length;
      const baseName = provider.label || providerValue;
      const generatedName = sameProviderCount > 0 ? `${baseName} ${sameProviderCount + 1}` : baseName;
      const clientRequestId =
        (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const payload = {
        name: generatedName,
        provider: providerValue,
        kind: provider.kind || "store",
        credentials: {},
        allow_extra_integration_purchase: Boolean(opts.allowExtraIntegrationPurchase),
        client_request_id: clientRequestId,
      };
      let data;
      try {
        data = await api.post("/integrations", payload);
      } catch (firstErr) {
        const isNetwork = !firstErr?.status || String(firstErr?.message || "").toLowerCase().includes("network");
        if (!isNetwork) throw firstErr;
        // Safe retry with the same request id (idempotent on backend).
        data = await api.post("/integrations", payload);
      }
      setIntegrations([...integrations, data]);
      setDialogOpen(false);
      setNewIntegration({ provider: "" });
      setSelectedStoreForCargo("");
      setCatalogFilter("all");
      setCatalogQuery("");
      toast.success("Entegrasyon eklendi!");
      return data;
    } catch (error) {
      const msg = String(error?.message || "Entegrasyon eklenemedi");
      if (!opts.allowExtraIntegrationPurchase && error?.status === 403 && msg.toLowerCase().includes("tekil ücretlendirme")) {
        const ok = window.confirm(`${msg}\n\nEk entegrasyon için tekil ücretlendirme ile devam etmek ister misiniz?`);
        if (ok) {
          return await createIntegrationForProvider(providerValue, { allowExtraIntegrationPurchase: true });
        }
      }
      if (error?.status === 402 && msg.toLowerCase().includes("intent:")) {
        const intentId = msg.split("intent:").pop()?.trim();
        if (intentId) {
          toast.info("Ödeme sayfasına yönlendiriliyorsunuz.");
          navigate(`/panel/hesabim/faturalandirma?view=payment&intent=${encodeURIComponent(intentId)}`);
          return null;
        }
      }
      toast.error(msg);
      return null;
    } finally {
      setAddingIntegration(false);
    }
  };

  const hasRemainingLimitForKind = (kind) => {
    const remaining = usageSummary?.remaining || {};
    const toNum = (v) => Number(v ?? 0);
    if (kind === "accounting") return toNum(remaining.accounting_integrations) > 0;
    if (kind === "store") return toNum(remaining.store_integrations) > 0 && toNum(remaining.integrations) > 0;
    if (kind === "marketplace") return toNum(remaining.marketplace_integrations) > 0 && toNum(remaining.integrations) > 0;
    if (kind === "cargo") return toNum(remaining.cargo_integrations) > 0 && toNum(remaining.integrations) > 0;
    return toNum(remaining.integrations) > 0;
  };

  const handleDelete = async (id) => {
    if (!canManageIntegrations) {
      toast.error("Bu işlem için entegrasyon yönetim yetkiniz yok.");
      return;
    }
    const target = integrations.find((i) => i.id === id) || { id };
    setDeleteTarget(target);
    setDeleteReason("");
    setDeleteNote("");
    setDeleteSubscriptionToo(false);
  };

  const confirmDelete = async () => {
    const id = deleteTarget?.id;
    if (!id) return;
    setDeleteSubmitting(true);
    try {
      try {
        await api.post(`/integrations/${id}/delete-feedback`, {
          reason: deleteReason || null,
          note: deleteNote || null,
          delete_subscription_too: Boolean(deleteSubscriptionToo),
        });
      } catch (feedbackError) {
        if (feedbackError?.status !== 404) throw feedbackError;
      }

      try {
        await api.delete(`/integrations/${id}`);
      } catch (deleteError) {
        if (deleteError?.status !== 404) throw deleteError;
      }

      const next = integrations.filter((i) => i.id !== id);
      setIntegrations(next);
      sessionStorage.setItem(INTEGRATIONS_CACHE_KEY, JSON.stringify(next));
      sessionStorage.setItem(INTEGRATIONS_CACHE_META_KEY, JSON.stringify({ ts: Date.now() }));
      await fetchIntegrations({ force: true });
      toast.success("Entegrasyon silindi");
      setDeleteTarget(null);
    } catch (error) {
      toast.error(error.message || "Entegrasyon silinemedi");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const proceedToBillingFromIntegration = () => {
    if (!pendingProvider || !selectedBillingPlan) {
      toast.error("Lütfen paket seçin.");
      return;
    }
    (async () => {
      if (packageDialogMode === "over_limit") {
        const extraIntegration = selectedBillingPlan === "extra_integration";
        const planKey = extraIntegration ? "starter" : selectedBillingPlan;
        const source = extraIntegration ? "extra_integration" : "upgrade";
        try {
          const res = await api.post("/billing/checkout-intent", {
            plan_key: planKey,
            billing_cycle: selectedBillingCycle,
            source,
            provider: pendingProvider.value,
            integration_kind: pendingProvider.kind || "store",
            integration_id: "",
            redirect_to: "/panel/entegrasyonlar",
          });
          setPackageDialogOpen(false);
          setDialogOpen(false);
          if (res?.checkout_intent_id) {
            navigate(`/panel/hesabim/faturalandirma?view=payment&intent=${encodeURIComponent(res.checkout_intent_id)}`);
            return;
          }
          navigate("/panel/hesabim/faturalandirma?view=payment");
          return;
        } catch (err) {
          toast.error(err?.message || "Ödeme kaydı oluşturulamadı");
          return;
        }
      }
      const created = await createIntegrationForProvider(pendingProvider.value);
      if (!created?.id) return;
      try {
        await api.post("/billing/checkout-intent", {
          plan_key: selectedBillingPlan,
          billing_cycle: selectedBillingCycle,
          source: "integration",
          provider: pendingProvider.value,
          integration_kind: pendingProvider.kind || "store",
          integration_id: created.id,
          redirect_to: `/panel/entegrasyonlar/${created.id}/ayarlar`,
        });
      } catch {
        // intent not critical for setup route; user can continue integration setup.
      }
      setPackageDialogOpen(false);
      setDialogOpen(false);
      navigate(`/panel/entegrasyonlar/${created.id}/ayarlar`);
    })();
  };

  const packageDialogOptions = useMemo(() => {
    const base = Array.isArray(billingPackages) ? billingPackages : [];
    if (packageDialogMode !== "over_limit") {
      if (!isTrialUser && ["starter", "growth", "pro", "enterprise"].includes(currentPlan)) {
        const rank = { starter: 1, growth: 2, pro: 2, enterprise: 3 };
        const currentRank = rank[currentPlan] || 0;
        const filtered = base.filter((p) => {
          const key = String(p.key || "").toLowerCase();
          const planRank = rank[key] || 0;
          return planRank > currentRank;
        });
        return filtered.length ? filtered : base;
      }
      return base;
    }

    const growth = base.find((p) => {
      const key = String(p.key || "").toLowerCase();
      return key === "growth" || key === "pro";
    });
    const enterprise = base.find((p) => String(p.key || "").toLowerCase() === "enterprise");
    const extra = {
      key: "extra_integration",
      name: "+1 Entegrasyon",
      description: "Mevcut paketinize tekil entegrasyon ekleyin.",
      monthly_price: Number(base.find((p) => String(p.key || "").toLowerCase() === "starter")?.monthly_price || 0),
      yearly_price: Number(base.find((p) => String(p.key || "").toLowerCase() === "starter")?.yearly_price || 0),
    };

    let options;
    if (currentPlan === "growth" || currentPlan === "pro") {
      options = [extra, ...(enterprise ? [enterprise] : [])];
    } else if (currentPlan === "starter") {
      options = [extra, ...(growth ? [growth] : []), ...(enterprise ? [enterprise] : [])];
    } else {
      options = [extra, ...base];
    }

    // Yıllık aktif abonelikte aylık sekmede sadece +1 entegrasyon gösterilir.
    if (serverSubscriptionCycle === "yearly" && selectedBillingCycle === "monthly") {
      return options.filter((x) => x.key === "extra_integration");
    }
    return options;
  }, [billingPackages, packageDialogMode, currentPlan, isTrialUser, serverSubscriptionCycle, selectedBillingCycle]);

  const isOverLimitUpgradeSelection =
    packageDialogMode === "over_limit" && selectedBillingPlan && selectedBillingPlan !== "extra_integration";

  useEffect(() => {
    if (!packageDialogOpen) return;
    if (!packageDialogOptions.length) return;
    setSelectedBillingPlan((prev) => {
      const exists = packageDialogOptions.some((x) => x.key === prev);
      if (exists) return prev;
      return packageDialogOptions[0].key;
    });
  }, [packageDialogOpen, packageDialogOptions]);

  useEffect(() => {
    if (!packageDialogOpen) return;
    if (isOverLimitUpgradeSelection && selectedBillingCycle !== "yearly") {
      setSelectedBillingCycle("yearly");
    }
  }, [packageDialogOpen, isOverLimitUpgradeSelection, selectedBillingCycle]);

  const resolvePlanFromRow = (row) => {
    const planRaw = String(
      row?.plan_key
      || row?.subscription_plan
      || row?.billing_plan
      || row?.credentials?.plan_key
      || ""
    ).trim().toLowerCase();
    if (!planRaw) return "";
    if (["starter", "pro", "enterprise"].includes(planRaw)) return planRaw;
    if (planRaw.includes("starter")) return "starter";
    if (planRaw.includes("enterprise")) return "enterprise";
    if (planRaw.includes("pro")) return "pro";
    return "";
  };

  const needsPaymentCompletion = (row) => {
    const integrationStatus = String(row?.status || "").trim().toLowerCase();
    const billingStatus = String(
      row?.billing_status
      || row?.payment_status
      || row?.subscription_status
      || row?.credentials?.billing_status
      || ""
    ).trim().toLowerCase();
    if (["pending_payment", "payment_pending", "awaiting_payment", "unpaid"].includes(billingStatus)) return true;
    if (["pending_payment", "payment_pending", "awaiting_payment"].includes(integrationStatus)) return true;
    if (row?.credentials?.checkout_intent_id && integrationStatus !== "success") return true;
    return false;
  };

  const goToBillingForRow = (row) => {
    const plan = resolvePlanFromRow(row);
    const cycle = String(row?.billing_cycle || row?.credentials?.billing_cycle || "monthly").toLowerCase();
    const params = new URLSearchParams({
      source: "integration",
      provider: String(row?.provider || "").toLowerCase(),
      kind: String(row?.kind || "store").toLowerCase(),
      next: "/panel/entegrasyonlar",
    });
    if (plan) params.set("plan", plan);
    if (cycle === "yearly" || cycle === "monthly") params.set("cycle", cycle);
    navigate(`/panel/hesabim/faturalandirma?${params.toString()}`);
  };

  // Cargo setup handlers
  const openCargoDialog = (integration, forceProvider = null, startAtStep = 1, forceConfigured = null) => {
    if (!canManageIntegrations) {
      toast.error("Bu işlem için entegrasyon yönetim yetkiniz yok.");
      return;
    }
    setCargoIntegration(integration);
    setCargoStep(startAtStep);
    const linkedProvider = forceProvider || integration?.cargo_provider || integration?.credentials?.cargo_provider || "hepsijet";
      const linkedLabel = cargoProvidersResolved.find((c) => c.value === linkedProvider)?.label || "HepsiJET";
    setCargoForm({
      provider: linkedProvider,
      integration_name: linkedLabel,
      username: "",
      password: "",
      sender_address_id: "",
      current_xdock: "",
    });
    setCargoAlreadyConfigured(forceConfigured === null ? Boolean(integration?.cargo_configured) : Boolean(forceConfigured));
    setShowPassword(false);
    setCargoDialogOpen(true);
  };

  const handleCargoStep1 = () => {
    if (!cargoForm.integration_name.trim()) {
      toast.error("Entegrasyon ismi gereklidir");
      return;
    }
    setCargoStep(2);
  };

  const handleCargoComplete = async () => {
    if (!canManageIntegrations) {
      toast.error("Bu işlem için entegrasyon yönetim yetkiniz yok.");
      return;
    }
    setCargoSaving(true);
    try {
      if (cargoForm.provider === "hepsijet") {
        if (!cargoForm.username || !cargoForm.password || !cargoForm.sender_address_id || !cargoForm.current_xdock) {
          toast.error("Lutfen tum alanlari doldurun");
          return;
        }
        const result = await api.post("/hepsijet/save-config", {
          username: cargoForm.username,
          password: cargoForm.password,
          sender_address_id: cargoForm.sender_address_id,
          current_xdock: cargoForm.current_xdock,
          integration_id: cargoIntegration.id,
          integration_name: cargoForm.integration_name,
        });
        if (!result?.success) {
          throw new Error(result?.message || "HepsiJet baglanamadi");
        }
      } else {
        await api.patch(`/integrations/${cargoIntegration.id}`, {
          cargo_provider: cargoForm.provider,
          cargo_configured: true,
          credentials: {
            ...(cargoIntegration?.credentials || {}),
            cargo_provider: cargoForm.provider,
            cargo_name: cargoForm.integration_name,
          },
        });
      }
      toast.success("Kargo entegrasyonu basariyla baglandi!");
      setCargoDialogOpen(false);
      await fetchIntegrations();
    } catch (err) {
      toast.error(err.message || "Kargo entegrasyonu olusturulamadi");
    } finally {
      setCargoSaving(false);
    }
  };

  const handleCargoTest = async () => {
    if (cargoForm.provider !== "hepsijet") {
      toast.success("Bu kargo saglayicisi icin API testi sonraki asamada eklenecek.");
      return;
    }
    setCargoTesting(true);
    try {
      // First save, then test
      await api.post("/hepsijet/save-config", {
        username: cargoForm.username,
        password: cargoForm.password,
        sender_address_id: cargoForm.sender_address_id,
        current_xdock: cargoForm.current_xdock,
        integration_id: cargoIntegration.id,
        integration_name: cargoForm.integration_name,
      });
      const result = await api.post("/hepsijet/test");
      if (result.success) {
        toast.success(result.message || "Baglanti basarili!");
      } else {
        toast.error(result.message || "Baglanti hatasi");
      }
    } catch (err) {
      toast.error(err.message || "Baglanti test edilemedi");
    } finally {
      setCargoTesting(false);
    }
  };

  const handleCargoRemove = async () => {
    if (!canManageIntegrations) {
      toast.error("Bu işlem için entegrasyon yönetim yetkiniz yok.");
      return;
    }
    if (!cargoIntegration) return;
    setCargoSaving(true);
    try {
      if (cargoForm.provider === "hepsijet") {
        await api.post("/hepsijet/disconnect", { integration_id: cargoIntegration.id });
      } else {
        await api.patch(`/integrations/${cargoIntegration.id}`, {
          cargo_provider: null,
          cargo_configured: false,
          credentials: {
            ...(cargoIntegration?.credentials || {}),
            cargo_provider: null,
            cargo_name: null,
          },
        });
      }
      toast.success("Kargo baglantisi kaldirildi");
      setCargoDialogOpen(false);
      await fetchIntegrations();
    } catch (err) {
      toast.error(err.message || "Kargo baglantisi kaldirilamadi");
    } finally {
      setCargoSaving(false);
    }
  };

  const storeRowsForCargo = useMemo(
    () =>
      integrations.filter(
        (x) => {
          const provider = (x.provider || "").toLowerCase();
          if (x.kind === "store") return true;
          if (storeProviderSet.has(provider)) return true;
          // Fallback: if kind is missing, treat non-cargo providers as store candidates.
          if (!x.kind && provider && !cargoProviderSet.has(provider)) return true;
          return false;
        }
      ),
    [integrations]
  );

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    const visible = integrations.filter((x) => x.kind !== "cargo");
    if (!s) return visible;
    return visible.filter((r) =>
      `${r.id} ${r.name} ${r.provider}`.toLowerCase().includes(s)
    );
  }, [q, integrations]);

  const cargoRows = useMemo(() => integrations.filter((x) => x.kind === "cargo"), [integrations]);

  const linkedCargoMap = useMemo(() => {
    const byStore = new Map();
    const storeRows = integrations.filter((x) => x.kind === "store" || x.kind === "marketplace");

    storeRows.forEach((store) => {
      const items = [];
      const providerSeen = new Set();
      const addItem = (item) => {
        const providerKey = String(item?.provider || "").toLowerCase().trim();
        if (!providerKey) return;
        // For a single store, show each cargo provider only once even if it comes
        // from legacy store flags + global config + child cargo integrations.
        if (providerSeen.has(providerKey)) return;
        providerSeen.add(providerKey);
        items.push(item);
      };

      // legacy/single binding on store row
      if (store?.cargo_provider) {
        addItem({
          id: `store-${store.id}-${store.cargo_provider}`,
          provider: store.cargo_provider,
          label: cargoProvidersResolved.find((c) => c.value === store.cargo_provider)?.label || store.cargo_provider,
          configured: Boolean(store.cargo_configured),
          failed: !store.cargo_configured,
          settingsId: store.id,
        });
      }

      // explicit child cargo integrations bound to this store
      cargoRows.forEach((c) => {
        const parentId =
          c?.credentials?.parent_integration_id ||
          c?.credentials?.store_integration_id ||
          c?.credentials?.integration_id ||
          c?.parent_integration_id ||
          c?.integration_id;
        if (!parentId || parentId !== store.id) return;
        addItem({
          id: c.id,
          storeId: store.id,
          provider: c.provider,
          label: c.name || cargoProvidersResolved.find((x) => x.value === c.provider)?.label || c.provider,
          configured: c.status === "success",
          failed: c.status !== "success",
          settingsId: c.id,
        });
      });

      // HepsiJet global config can be linked to a specific store
      if (hepsijetConfig?.configured && hepsijetConfig?.integration_id === store.id) {
        addItem({
          id: `hj-${store.id}`,
          storeId: store.id,
          provider: "hepsijet",
          label: "HepsiJet",
          configured: true,
          failed: false,
          settingsId: store.id,
        });
      }

      byStore.set(store.id, items);
    });

    return byStore;
  }, [cargoProvidersResolved, integrations, cargoRows, hepsijetConfig]);

  const modalCatalogRows = useMemo(() => {
    const s = catalogQuery.trim().toLowerCase();
    return addableCatalog.filter((x) => {
      const matchKind = catalogFilter === "all" ? true : x.kind === catalogFilter;
      const matchSearch = !s ? true : `${x.label} ${x.value}`.toLowerCase().includes(s);
      return matchKind && matchSearch;
    });
  }, [addableCatalog, catalogFilter, catalogQuery]);

  return (
    <TooltipProvider>
      <div className="w-full" data-testid="integrations-page">
        <div className="page-shell">
          {/* Header */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="app-page-header">
              <h1 className="app-page-title">Entegrasyonlar</h1>
              <p className="app-page-subtitle">Kanalları bağla, siparişleri listele, ürün eşleştir ve kurulumları yönet.</p>
            </div>
            <Dialog
              open={dialogOpen}
              onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) {
                  setCatalogFilter("all");
                  setCatalogQuery("");
                  setNewIntegration({ provider: "" });
                  setSelectedStoreForCargo("");
                }
              }}
            >
              <DialogTrigger asChild>
                <Button
                  disabled={!canManageIntegrations}
                  className={cn("hidden sm:inline-flex", !canManageIntegrations ? "bg-muted hover:bg-muted" : "")}
                  data-testid="add-integration-btn"
                >
                  <Plus className="h-4 w-4" />
                  Yeni Entegrasyon Ekle
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-6xl w-[92vw] h-[88vh] overflow-hidden p-0 rounded-2xl border border-border shadow-2xl">
                <DialogHeader className="px-6 py-4 border-b border-border bg-gradient-to-b from-slate-50 to-white">
                  <DialogTitle>Yeni Entegrasyon Ekle</DialogTitle>
                  <DialogDescription>Pazaryeri ve E-Ticaret entegrasyonlarini ekleyin. Kargo baglantilari satirdan baglanir.</DialogDescription>
                </DialogHeader>
                <div className="h-[calc(88vh-92px)] overflow-y-auto px-6 pb-6">
                  <div className="sticky top-0 z-10 -mx-6 px-6 pt-4 pb-3 bg-card/95 backdrop-blur border-b border-border">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="inline-flex rounded-lg border border-border bg-background p-1 shadow-sm">
                        {[
                          { key: "all", label: "Tumu" },
                          { key: "marketplace", label: "Pazaryeri" },
                          { key: "cargo", label: "Kargo" },
                          { key: "store", label: "E-Ticaret" },
                        ].map((f) => (
                          <button
                            key={f.key}
                            type="button"
                            onClick={() => setCatalogFilter(f.key)}
                            className={cn(
                              "h-8 rounded-md px-3 text-xs font-medium transition-colors",
                              catalogFilter === f.key
                                ? "bg-[#1487a3] text-white shadow-sm"
                                : "text-muted-foreground hover:bg-muted"
                            )}
                          >
                            {f.label}
                          </button>
                        ))}
                      </div>
                      <div className="relative w-full sm:w-80">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          value={catalogQuery}
                          onChange={(e) => setCatalogQuery(e.target.value)}
                        placeholder=""
                          className="pl-9 h-10 bg-background"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-4">
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                      {modalCatalogRows.map((p) => {
                        const selected = newIntegration.provider === p.value;
                        return (
                          <button
                            key={p.value}
                            type="button"
                            title={p.label}
                            onClick={() => {
                              if (p.kind === "cargo") {
                                setNewIntegration({ provider: p.value });
                                setSelectedStoreForCargo("");
                                return;
                              }
                              if (hasRemainingLimitForKind(p.kind)) {
                                (async () => {
                                  const created = await createIntegrationForProvider(p.value);
                                  if (!created?.id) return;
                                  setDialogOpen(false);
                                  navigate(`/panel/entegrasyonlar/${created.id}/ayarlar`);
                                })();
                                return;
                              }
                              setPendingProvider(p);
                              setPackageDialogMode("over_limit");
                              setPackageDialogOpen(true);
                            }}
                            className={cn(
                              "flex h-36 items-center justify-center rounded-xl border bg-card p-3 transition-all",
                              selected
                                ? "border-[#1487a3] bg-sky-50 ring-2 ring-sky-100"
                                : "border-border hover:bg-muted/40 hover:shadow-sm"
                            )}
                          >
                            <ProviderLogo provider={p.value} size="large" framed={false} />
                          </button>
                        );
                      })}
                    </div>
                    {modalCatalogRows.length === 0 && (
                      <div className="py-12 text-center text-sm text-muted-foreground">Filtreye uygun entegrasyon bulunamadi.</div>
                    )}
                  </div>

                  {effectiveCatalog.find((x) => x.value === newIntegration.provider)?.kind === "cargo" ? (
                    <div className="space-y-2 mt-5">
                      <Label>Kargonun Baglanacagi E-Ticaret</Label>
                      {storeRowsForCargo.length ? (
                        <div className="flex flex-wrap gap-2 rounded-lg border border-border bg-background p-2">
                          {storeRowsForCargo.map((s) => (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => {
                                setSelectedStoreForCargo(s.id);
                                const targetStore = storeRowsForCargo.find((x) => x.id === s.id);
                                const selectedCargo = newIntegration.provider;
                                if (!targetStore || !selectedCargo) return;
                                setDialogOpen(false);
                                openCargoDialog(targetStore, selectedCargo, 2, false);
                                toast.success("Kargo secildi. Kurulum adimina yonlendiriliyorsunuz.");
                              }}
                              className={cn(
                                "h-8 rounded-md px-3 text-xs font-medium transition-colors",
                                selectedStoreForCargo === s.id
                                  ? "bg-[#1487a3] text-white"
                                  : "bg-muted text-foreground hover:bg-muted/80"
                              )}
                            >
                              {s.name}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="h-10 rounded-lg border border-border bg-background px-3 text-sm text-muted-foreground flex items-center">
                          Magaza bulunamadi
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>

                <div className="border-t border-border px-6 py-3 bg-muted/50/80 text-xs text-muted-foreground">
                  {addingIntegration
                    ? "Ekleniyor..."
                    : "Logo secimi aninda ekleme yapar. Kargo seciminde bir sonraki adim magazaya tiklamaktir."}
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Search */}
          <div className="mt-5 rounded-lg border border-border bg-card p-4">
            <div className="flex items-center gap-2">
              <div className="inline-flex h-10 w-full items-center gap-2 rounded-lg border border-border bg-background px-3">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  placeholder="Ara"
                  data-testid="search-integrations-input"
                />
              </div>
              <Button
                type="button"
                size="icon"
                onClick={() => setDialogOpen(true)}
                disabled={!canManageIntegrations}
                className={cn("inline-flex sm:hidden h-10 w-10 shrink-0", !canManageIntegrations ? "bg-muted hover:bg-muted" : "")}
                title="Yeni Entegrasyon Ekle"
                data-testid="add-integration-btn-mobile"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Table */}
          <div className="mt-5 overflow-hidden rounded-lg border border-border bg-card">
            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-border">
              {loading ? (
                <div className="px-4 py-10 text-center text-sm text-muted-foreground">Yukleniyor...</div>
              ) : rows.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-muted-foreground">Sonuc bulunamadi.</div>
              ) : (
                rows.map((r) => {
                  const isStoreOrMarket = r.kind === "store" || r.kind === "marketplace";
                  const rowNeedsPayment = needsPaymentCompletion(r);
                  const settingsPath =
                    r.provider === "hepsijet" || r.provider === "yurtici"
                      ? `/panel/kargo/${r.provider}`
                      : `/panel/entegrasyonlar/${r.id}/ayarlar`;
                  const canAddCargo = r.kind === "store" || r.kind === "marketplace" || storeProviderSet.has((r.provider || "").toLowerCase());
                  const canShowCargoAction = !marketplaceSelfCargoProviders.has(String(r.provider || "").toLowerCase());
                  const linkedCargoes = linkedCargoMap.get(r.id) || [];
                  const specialCargoLink = linkedCargoes.find((cargo) => cargo.provider === "hepsijet" || cargo.provider === "yurtici");
                  const hasSpecialCargoLink = Boolean(specialCargoLink);
                  return (
                    <div key={`m-${r.id}`} className="p-3">
                      <div className="rounded-xl border border-border/80 bg-card p-3 shadow-sm">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <ProviderLogo provider={r.provider} size="small" />
                            <div className="min-w-0">
                              <div className="truncate text-sm font-semibold text-foreground">{r.badgeText || r.name}</div>
                              <div className="mt-0.5 text-[11px] text-muted-foreground">
                                {isStoreOrMarket ? "E-Ticaret Entegrasyonu" : r.kind === "cargo" ? "Kargo Entegrasyonu" : "Muhasebe Entegrasyonu"}
                              </div>
                              <div className="mt-0.5 text-[11px] text-muted-foreground">#{r.integration_no || "-"}</div>
                            </div>
                          </div>
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => navigate(settingsPath)}
                              className="h-8 w-8 rounded-lg bg-muted/50 ring-1 ring-border hover:bg-muted/60 inline-flex items-center justify-center"
                              title="Ayarlar"
                            >
                              <Settings2 className="h-3.5 w-3.5 text-muted-foreground" />
                            </button>
                            <StatusPill status={r.status} />
                            <button
                              type="button"
                              disabled={!canManageIntegrations}
                              onClick={() => handleDelete(r.id)}
                              className={cn(
                                "h-8 w-8 rounded-lg ring-1 inline-flex items-center justify-center",
                                canManageIntegrations
                                  ? "bg-rose-50 text-rose-700 ring-rose-200 hover:bg-rose-100"
                                  : "bg-muted/60 text-muted-foreground ring-border cursor-not-allowed"
                              )}
                              title="Sil"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>

                        <div className="mt-3 grid grid-cols-3 gap-2">
                          {rowNeedsPayment ? (
                            <button
                              type="button"
                              onClick={() => goToBillingForRow(r)}
                              className="h-9 w-full rounded-lg bg-amber-500 px-2 text-[11px] font-semibold text-white hover:bg-amber-600 inline-flex items-center justify-center gap-1"
                            >
                              Ödemeyi Tamamla
                            </button>
                          ) : null}
                          {r.status !== "failed" && isStoreOrMarket ? (
                            <button
                              type="button"
                              onClick={() => navigate(`/panel/entegrasyonlar/${r.id}/siparisler`)}
                              className="h-9 w-full rounded-lg bg-muted/50 px-2 text-[11px] font-medium text-foreground ring-1 ring-border hover:bg-muted/60 inline-flex items-center justify-center gap-1"
                            >
                              <ShoppingCart className="h-3.5 w-3.5" /> Siparis
                            </button>
                          ) : null}
                          {r.status !== "failed" && isStoreOrMarket ? (
                            <button
                              type="button"
                              onClick={() => navigate(`/panel/entegrasyonlar/${r.id}/urun-eslestirme`)}
                              className="h-9 w-full rounded-lg bg-muted/50 px-2 text-[10px] font-medium text-foreground ring-1 ring-border hover:bg-muted/60 inline-flex items-center justify-center gap-1"
                            >
                              <Package className="h-3.5 w-3.5" /> Ürün Eşleştir
                            </button>
                          ) : null}
                          {isStoreOrMarket && hasSpecialCargoLink ? (
                            <button
                              type="button"
                              onClick={() => specialCargoLink && navigate(`/panel/kargo/${specialCargoLink.provider}`)}
                              className="h-9 w-full rounded-lg bg-muted/50 px-2 text-[10px] font-medium text-foreground ring-1 ring-border hover:bg-muted/60 inline-flex items-center justify-center gap-1"
                            >
                              <Settings2 className="h-3.5 w-3.5" /> Kargo Ayarla
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Header */}
            <div className="hidden md:grid grid-cols-12 border-b border-border bg-muted/50 px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
              <div className="col-span-6">Entegrasyon Ismi</div>
              <div className="col-span-5 text-right">Islemler</div>
              <div className="col-span-1 text-right">Durum</div>
            </div>

            {/* Body */}
            <div className="hidden md:block divide-y divide-border max-h-[calc(100vh-320px)] overflow-y-auto">
              {loading ? (
                <div className="px-4 py-10 text-center text-sm text-muted-foreground">Yukleniyor...</div>
              ) : rows.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-muted-foreground">Sonuc bulunamadi.</div>
              ) : (
                rows.map((r) => {
                  const isStoreOrMarket = r.kind === "store" || r.kind === "marketplace";
                  const isCargo = r.kind === "cargo";
                  const rowNeedsPayment = needsPaymentCompletion(r);
                  const settingsPath =
                    r.provider === "hepsijet" || r.provider === "yurtici"
                      ? `/panel/kargo/${r.provider}`
                      : `/panel/entegrasyonlar/${r.id}/ayarlar`;
                  const canShowCargoAction = !marketplaceSelfCargoProviders.has(String(r.provider || "").toLowerCase());
                  return (
                    <div
                      key={r.id}
                      className={cn(
                        "grid grid-cols-12 items-center px-4 py-3 transition-colors hover:bg-muted/50",
                        r.status === "failed" && "bg-rose-50/40 hover:bg-rose-50/60"
                      )}
                      data-testid={`integration-row-${r.id}`}
                    >
                      {/* Name */}
                      <div className="col-span-6 flex items-center gap-3">
                        <ProviderLogo provider={r.provider} />
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="truncate text-sm font-semibold text-foreground">{r.badgeText || r.name}</div>
                            <span className="text-[11px] text-muted-foreground">#{r.integration_no || "-"}</span>
                          </div>
                          {isStoreOrMarket ? (
                            <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                              <span className="inline-flex items-center gap-1">
                                <Package className="h-3.5 w-3.5" /> Urunler
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <ShoppingCart className="h-3.5 w-3.5" /> Siparisler
                              </span>
                            </div>
                          ) : isCargo ? (
                            <div className="mt-1 text-[11px] text-muted-foreground">
                              Kargo saglayicisi baglantisi.
                            </div>
                          ) : (
                            <div className="mt-1 text-[11px] text-muted-foreground">
                              Muhasebe baglantisi.
                            </div>
                          )}
                          {(r.kind === "store" || r.kind === "marketplace") && (linkedCargoMap.get(r.id)?.length || 0) > 0 ? (
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              {linkedCargoMap.get(r.id).map((cargo) => (
                                <button
                                  key={cargo.id}
                                  type="button"
                                  onClick={() => {
                                    if (cargo.provider !== "hepsijet" && cargo.provider !== "yurtici") {
                                      openCargoDialog(r, cargo.provider, 2, cargo.configured);
                                      return;
                                    }
                                    if (cargo.configured) {
                                      if (cargo.provider === "hepsijet") {
                                        navigate("/panel/kargo/hepsijet");
                                        return;
                                      }
                                      if (cargo.provider === "yurtici") {
                                        navigate("/panel/kargo/yurtici");
                                        return;
                                      }
                                      navigate(`/panel/entegrasyonlar/${cargo.settingsId}/ayarlar`);
                                      return;
                                    }
                                    navigate(`/panel/entegrasyonlar/${cargo.settingsId}/ayarlar`);
                                  }}
                                  className={cn(
                                    "inline-flex items-center gap-2 rounded-md border px-2 py-1 text-[11px] font-medium",
                                    cargo.configured
                                      ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                      : "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                                  )}
                                  title={cargo.configured ? "Ayarlar" : "Kurulumu Tamamla"}
                                >
                                  <ProviderLogo provider={cargo.provider} size="small" />
                                  <span>{cargo.label}</span>
                                  <span className="opacity-70">/</span>
                                  <span>{cargo.configured ? "Ayarlar" : "Kurulumu Tamamla"}</span>
                                </button>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="col-span-5 flex items-center justify-end gap-2">
                        {rowNeedsPayment ? (
                          <button
                            type="button"
                            onClick={() => goToBillingForRow(r)}
                            className="h-9 rounded-xl bg-amber-500 px-3 text-xs font-semibold text-white hover:bg-amber-600 inline-flex items-center"
                          >
                            Ödemeyi Tamamla
                          </button>
                        ) : null}
                        {r.status !== "failed" && isStoreOrMarket ? (
                          <>
                            <button
                              type="button"
                              onClick={() => navigate(`/panel/entegrasyonlar/${r.id}/siparisler`)}
                              className="h-9 rounded-lg bg-card px-3 text-xs font-medium text-foreground ring-1 ring-border hover:bg-muted inline-flex items-center"
                              data-testid={`orders-btn-${r.id}`}
                            >
                              Siparisleri Listele
                            </button>
                            <button
                              type="button"
                              onClick={() => navigate(`/panel/entegrasyonlar/${r.id}/urun-eslestirme`)}
                              className="h-9 rounded-lg bg-card px-3 text-xs font-medium text-foreground ring-1 ring-border hover:bg-muted inline-flex items-center"
                              data-testid={`products-btn-${r.id}`}
                            >
                              Ürün Eşleştir
                            </button>
                          </>
                        ) : r.status === "failed" ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/panel/entegrasyonlar/${r.id}/ayarlar`)}
                            className="h-9 rounded-xl bg-rose-600 px-3 text-xs font-semibold text-white hover:bg-rose-700 inline-flex items-center"
                          >
                            Kurulumu Tamamla
                          </button>
                        ) : null}

                        <button
                          type="button"
                          onClick={() => {
                            if (r.kind === "cargo") {
                              const parentId =
                                r?.credentials?.parent_integration_id ||
                                r?.credentials?.store_integration_id ||
                                r?.credentials?.integration_id ||
                                r?.parent_integration_id;
                              const parent = integrations.find((x) => x.id === parentId && x.kind === "store");
                              if (parent) {
                                openCargoDialog(parent, r.provider, 2, r.status === "success");
                                return;
                              }
                            }
                            if (r.provider === "hepsijet") {
                              navigate(settingsPath);
                              return;
                            }
                            navigate(settingsPath);
                          }}
                          className="h-9 w-9 rounded-lg bg-card ring-1 ring-border hover:bg-muted inline-flex items-center justify-center"
                          title="Ayarlar"
                          data-testid={`settings-btn-${r.id}`}
                        >
                          <Settings2 className="h-4 w-4 text-muted-foreground" />
                        </button>


                        <button
                          type="button"
                          disabled={!canManageIntegrations}
                          onClick={() => handleDelete(r.id)}
                          className={cn(
                            "h-9 w-9 rounded-lg ring-1 inline-flex items-center justify-center",
                            canManageIntegrations
                              ? "bg-card ring-border hover:bg-muted"
                              : "bg-muted/60 ring-border cursor-not-allowed"
                          )}
                          title="Sil"
                          data-testid={`delete-btn-${r.id}`}
                        >
                          <Trash2 className={cn("h-4 w-4", canManageIntegrations ? "text-muted-foreground" : "text-muted-foreground")} />
                        </button>
                      </div>

                      {/* Status */}
                      <div className="col-span-1 flex items-center justify-end">
                        <StatusPill
                          status={r.status}
                          onClick={() => {
                            if (r.status !== "success" && r.status !== "failed") {
                              navigate(settingsPath);
                            }
                          }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Cargo Setup Dialog */}
        <Dialog open={cargoDialogOpen} onOpenChange={setCargoDialogOpen}>
          <DialogContent className="max-w-xl">
            {cargoStep === 1 ? (
              <>
                <DialogHeader>
                  <DialogTitle className="text-lg font-bold">Kargo Entegrasyonu Ayarlari</DialogTitle>
                  <DialogDescription>Kargo servisini magaza entegrasyonunuza baglayin.</DialogDescription>
                </DialogHeader>
                <div className="space-y-5 pt-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label>Magaza Secimi</Label>
                      <Tooltip>
                        <TooltipTrigger>
                          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>Kargo entegrasyonu baglanacak magaza</TooltipContent>
                      </Tooltip>
                    </div>
                    <div className="flex items-center h-10 rounded-lg border border-border bg-muted/50 px-3">
                      <Globe className="h-4 w-4 text-purple-600 mr-2 shrink-0" />
                      <span className="text-sm text-foreground font-medium truncate">
                        {cargoIntegration?.name || "-"}
                      </span>
                      <ProviderPill provider={cargoIntegration?.provider || ""} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label>Kargo Saglayici</Label>
                      <Tooltip>
                        <TooltipTrigger>
                          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>Bu magazaya baglanacak kargo saglayici</TooltipContent>
                      </Tooltip>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {cargoProvidersResolved.map((cp) => (
                        <button
                          key={cp.value}
                          type="button"
                          title={cp.label}
                          onClick={() =>
                            setCargoForm((prev) => ({
                              ...prev,
                              provider: cp.value,
                              integration_name: cp.label,
                            }))
                          }
                          className={cn(
                            "flex items-center justify-center rounded-lg border p-2 transition-colors",
                            cargoForm.provider === cp.value
                              ? "border-[#1487a3] bg-sky-50"
                              : "border-border bg-card hover:bg-muted/50"
                          )}
                        >
                          <ProviderLogo provider={cp.value} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Baglanti Ismi</Label>
                    <Input
                      value={cargoForm.integration_name}
                      onChange={(e) => setCargoForm({ ...cargoForm, integration_name: e.target.value })}
                      placeholder="Orn: HepsiJet"
                      data-testid="cargo-integration-name-input"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleCargoStep1}
                    className="w-full h-11 rounded-lg bg-[#1487a3] text-white text-sm font-semibold hover:bg-[#1e7ab3] transition-colors"
                    data-testid="cargo-step1-btn"
                  >
                    Entegrasyonu Olustur
                  </button>
                </div>
              </>
            ) : (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setCargoStep(1)}
                      className="h-8 w-8 rounded-lg hover:bg-muted inline-flex items-center justify-center transition-colors"
                      data-testid="cargo-back-btn"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </button>
                    <div>
                      <DialogTitle className="text-lg font-bold">Baglanti Ayarlari</DialogTitle>
                      <DialogDescription>
                        {cargoForm.integration_name} entegrasyonu icin API bilgilerini girin.
                      </DialogDescription>
                    </div>
                  </div>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  {cargoForm.provider === "hepsijet" ? (
                    <>
                      {cargoAlreadyConfigured ? (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                          HepsiJet baglantisi aktif. Guvenlik icin API bilgileri gizlenir.
                        </div>
                      ) : (
                        <>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Label>Kullanici Adi (Username)</Label>
                              <Tooltip>
                                <TooltipTrigger>
                                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent>HepsiJet tarafindan verilen kullanici adi</TooltipContent>
                              </Tooltip>
                            </div>
                            <Input
                              value={cargoForm.username}
                              onChange={(e) => setCargoForm({ ...cargoForm, username: e.target.value })}
                              placeholder="ornek_integration"
                              data-testid="cargo-username-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Label>Sifre (Password)</Label>
                              <Tooltip>
                                <TooltipTrigger>
                                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent>HepsiJet tarafindan verilen sifre</TooltipContent>
                              </Tooltip>
                            </div>
                            <div className="relative">
                              <Input
                                type={showPassword ? "text" : "password"}
                                value={cargoForm.password}
                                onChange={(e) => setCargoForm({ ...cargoForm, password: e.target.value })}
                                placeholder="********"
                                className="pr-10"
                                data-testid="cargo-password-input"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                data-testid="cargo-toggle-password"
                              >
                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Label>Firma Adres Kodu (Company Address ID)</Label>
                            </div>
                            <Input
                              value={cargoForm.sender_address_id}
                              onChange={(e) => setCargoForm({ ...cargoForm, sender_address_id: e.target.value })}
                              placeholder="fiki-fikirizm-539"
                              data-testid="cargo-address-id-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Label>Firma Depo Kodu (CurrentXDock)</Label>
                            </div>
                            <Input
                              value={cargoForm.current_xdock}
                              onChange={(e) => setCargoForm({ ...cargoForm, current_xdock: e.target.value })}
                              placeholder="FIKIRIZMNARLIDEREUser"
                              data-testid="cargo-xdock-input"
                            />
                          </div>
                        </>
                      )}
                      <div className="flex gap-3 pt-2">
                        {!cargoAlreadyConfigured && (
                          <button
                            type="button"
                            onClick={handleCargoTest}
                            disabled={cargoTesting || !cargoForm.username || !cargoForm.password}
                            className="h-11 rounded-lg border border-border bg-card px-4 text-sm font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                            data-testid="cargo-test-btn"
                          >
                            {cargoTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                            Baglantiyi Test Et
                          </button>
                        )}
                        {!cargoAlreadyConfigured ? (
                          <button
                            type="button"
                            onClick={handleCargoComplete}
                            disabled={cargoSaving}
                            className="flex-1 h-11 rounded-lg bg-[#1487a3] text-white text-sm font-semibold hover:bg-[#1e7ab3] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                            data-testid="cargo-complete-btn"
                          >
                            {cargoSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                            Kurulumu Tamamla
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={handleCargoRemove}
                            disabled={cargoSaving}
                            className="flex-1 h-11 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                          >
                            {cargoSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                            Baglantiyi Kaldir
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
                        {cargoForm.integration_name} bu magazaya baglanacak.
                      </div>
                      <div className="flex gap-3 pt-2">
                        <button
                          type="button"
                          onClick={handleCargoComplete}
                          disabled={cargoSaving}
                          className="flex-1 h-11 rounded-lg bg-[#1487a3] text-white text-sm font-semibold hover:bg-[#1e7ab3] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                        >
                          {cargoSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                          Kargo Saglayiciyi Bagla
                        </button>
                        <button
                          type="button"
                          onClick={handleCargoRemove}
                          disabled={cargoSaving || !cargoAlreadyConfigured}
                          className="h-11 rounded-lg border border-rose-300 bg-rose-50 px-4 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                        >
                          Baglantiyi Kaldir
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => (!open ? setDeleteTarget(null) : null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Entegrasyonu silmek istediğinize emin misiniz?</DialogTitle>
              <DialogDescription>
                Bu entegrasyonu silerseniz bu entegrasyona bağlı tüm siparişleriniz silinecektir. Bu işlem geri alınamaz.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {deleteTarget?.name ? `"${deleteTarget.name}"` : "Seçili entegrasyon"} kalıcı olarak kaldırılacak.
            </div>
            <div className="space-y-2">
              <Label>Abonelik iptal sebebi</Label>
              <select
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm"
              >
                <option value="">Seçiniz</option>
                <option value="too_expensive">Fiyat yüksek</option>
                <option value="missing_feature">İhtiyaç duyduğum özellik yok</option>
                <option value="technical_issue">Teknik sorunlar yaşadım</option>
                <option value="not_needed">Artık ihtiyaç yok</option>
                <option value="other">Diğer</option>
              </select>
              <textarea
                value={deleteNote}
                onChange={(e) => setDeleteNote(e.target.value)}
                className="min-h-[80px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                placeholder="Kısa geri bildiriminizi paylaşın"
              />
              <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                <input type="checkbox" checked={deleteSubscriptionToo} onChange={(e) => setDeleteSubscriptionToo(e.target.checked)} />
                Entegrasyonla ilişkili abonelik de kapatılsın
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>
                Vazgeç
              </Button>
              <Button type="button" disabled={deleteSubmitting} className="bg-rose-600 hover:bg-rose-700 text-white" onClick={confirmDelete}>
                Evet, Sil
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={packageDialogOpen} onOpenChange={setPackageDialogOpen}>
          <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Paket Seçiniz</DialogTitle>
                  <DialogDescription>
                {packageDialogMode === "over_limit"
                  ? `${pendingProvider?.label || "Seçili entegrasyon"} için limitiniz dolu. Paketi yükseltebilir veya +1 entegrasyon satın alabilirsiniz.`
                  : `${pendingProvider?.label || "Seçili entegrasyon"} için paket seçin.`}
                  </DialogDescription>
                </DialogHeader>
            <div className="space-y-4">
              <div className="inline-flex rounded-full border border-border bg-muted p-1">
                <button
                  type="button"
                  onClick={() => setSelectedBillingCycle("yearly")}
                  className={cn("rounded-full px-4 py-1.5 text-xs font-semibold", selectedBillingCycle === "yearly" ? "bg-[#1487a3] text-white" : "text-muted-foreground")}
                >
                  Yıllık
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedBillingCycle("monthly")}
                  disabled={isOverLimitUpgradeSelection}
                  className={cn("rounded-full px-4 py-1.5 text-xs font-semibold", selectedBillingCycle === "monthly" ? "bg-[#1487a3] text-white" : "text-muted-foreground")}
                >
                  Aylık
                </button>
              </div>
              <div className="space-y-2">
                {packageDialogOptions.map((p) => {
                  const amount = selectedBillingCycle === "yearly" ? Number(p.yearly_price || 0) : Number(p.monthly_price || 0);
                  const selected = selectedBillingPlan === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => {
                        setSelectedBillingPlan(p.key);
                        if (packageDialogMode === "over_limit" && p.key !== "extra_integration") {
                          setSelectedBillingCycle("yearly");
                        }
                      }}
                      className={cn("w-full rounded-lg border p-3 text-left", selected ? "border-[#1487a3] bg-sky-50" : "border-border bg-card hover:bg-muted/40")}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold">{p.name}</div>
                          <div className="text-xs text-muted-foreground">{p.description}</div>
                          {p.key !== "extra_integration" && isTrialUser ? (
                            <div className="mt-1 inline-flex rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-600">15 Gün Ücretsiz Deneme</div>
                          ) : null}
                        </div>
                        <div className="text-sm font-semibold">{amount.toLocaleString("tr-TR")} ₺ / {selectedBillingCycle === "yearly" ? "Yıl" : "Ay"}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setPackageDialogOpen(false)}>Vazgeç</Button>
                <Button type="button" className="bg-[#1487a3] hover:bg-[#1f79ad] text-white" onClick={proceedToBillingFromIntegration}>
                  Devam Et
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </TooltipProvider>
  );
}
